import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material'
import { useOrgHeartbeatLink } from 'api/aws/heartbeat/useOrgHeartbeatLink'
import type { NewDeal } from 'types'

/**
 * The Heartbeat account this deal's contract will reach, beside the template.
 *
 * Signing links can't be generated until the account is linked, so an unlinked
 * account says so here in colour, before anyone goes looking for why the
 * button is disabled.
 */

interface HeartbeatAccountUsedProps {
  deal?: NewDeal | null
}

const chipSx = {
  height: 20,
  fontSize: 10.5,
  maxWidth: 320,
  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
}

const HeartbeatAccountUsed = ({ deal }: HeartbeatAccountUsedProps) => {
  const { linked, heartbeatAccountId, heartbeatAccountName, netsuiteId, isLoading, isError } = useOrgHeartbeatLink(
    deal?.PK
  )

  if (!deal?.PK) return null

  if (isLoading) {
    return <Skeleton variant="rounded" width={150} height={20} sx={{ borderRadius: 1 }} />
  }

  let chip: JSX.Element
  if (isError || linked === null) {
    chip = <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Unavailable</Typography>
  } else if (!linked) {
    chip = (
      <Tooltip
        title="This account has no Heartbeat account ID, so signing links can't be generated. Ask a Grobotic admin to link it in the accounts grid."
        enterDelay={400}
      >
        <Chip label="Not linked" size="small" color="error" variant="outlined" sx={{ ...chipSx, fontWeight: 700 }} />
      </Tooltip>
    )
  } else {
    const label = heartbeatAccountName ? `${heartbeatAccountId} · ${heartbeatAccountName}` : String(heartbeatAccountId)
    chip = (
      <Tooltip
        title={`Heartbeat account ${heartbeatAccountId}${heartbeatAccountName ? `, ${heartbeatAccountName}` : ''}. NetSuite ID: ${
          netsuiteId ?? 'none'
        }.`}
        enterDelay={400}
      >
        <Chip label={label} size="small" variant="outlined" sx={{ ...chipSx, fontWeight: 500 }} />
      </Tooltip>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>Heartbeat</Typography>
      {chip}
    </Box>
  )
}

export default HeartbeatAccountUsed
